/**
 * Facebook Pixel & Conversion API Service
 *
 * Architecture:
 *  - This service is the only one to load and init the pixels: the pixel code
 *    pasted in a category (PixelBalise) is NOT executed, otherwise every
 *    PageView would be sent twice and to every pixel of the store.
 *  - Pixels come from the product CATEGORIES only (the pixel of Paramétrage is
 *    not used): each event goes to the pixel of the product's category, via
 *    `fbq('trackSingle', pixelId, ...)` so each call targets exactly ONE pixel.
 *  - fbevents.js is only loaded when a category pixel is used.
 *  - fbq('init', pixelId) is called at most once per pixel per session.
 *  - Conversion API calls are proxied through the backend controller
 *    POST /api/FacebookConversion/event to keep the access_token secret; they
 *    are only sent for pixels that have a token on the server (hasPixelToken).
 *  - event_id is shared between browser pixel and CAPI for deduplication, and
 *    is derived from the order for Lead / InitiateCheckout / Purchase so a
 *    repeated event is deduplicated too.
 */

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';
let settingsLoader = null;

// fbq('init', '1234567890') in a pasted pixel code.
const PIXEL_CODE_ID = /fbq\(\s*['"]init['"]\s*,\s*['"](\d{6,20})['"]/;

// A repeated identical event within this delay is ignored (double render, double click).
const REPEAT_GUARD_MS = 3000;

/**
 * Pixel of a product category: its pixelId field, or the ID found in its pasted
 * pixel code. Returns { pixelId, hasToken } or null.
 */
export const categoryPixel = (category) => {
  if (!category) return null;
  const pixelId = String(category.pixelId || '').trim()
    || (String(category.pixelBalise || '').match(PIXEL_CODE_ID) || [])[1];
  if (!pixelId) return null;
  // hasPixelToken is sent by the API (the token itself never reaches the storefront).
  const hasToken = category.hasPixelToken ?? !!category.pixelToken;
  return { pixelId, hasToken };
};

const toNumber = (value) => {
  const n = parseFloat(value);
  return Number.isFinite(n) ? n : 0;
};

// [{ productId, quantity, price }] -> pixel "contents"
const toContents = (items) => items
  .map((i) => ({
    id: String(i.productId || i.id || ''),
    quantity: parseInt(i.quantity, 10) || 1,
    item_price: toNumber(i.price),
  }))
  .filter((i) => i.id);

const productData = (contents, value, currency) => ({
  content_type: 'product',
  content_ids: contents.map((c) => c.id),
  contents,
  num_items: contents.reduce((sum, c) => sum + c.quantity, 0),
  value: Math.round(toNumber(value) * 1000) / 1000,
  currency: currency || 'TND',
});

class FacebookConversionService {
  constructor() {
    this.initialized = false;
    /** TND for 1 USD (Paramétrage): event values are sent in USD */
    this.tauxDollar = null;
    this.initializationPromise = null;
    /** Set of pixel IDs that have already been fbq('init')-ed this session */
    this._initedPixels = new Set();
    /** Pixels that already had Advanced Matching (hashed PII) attached */
    this._amAppliedPixels = new Set();
    this._fbqLoadPromise = null;
    /** Last time each event key was sent (repeat guard) */
    this._recent = new Map();
  }

  /**
   * Normalize a phone number to E.164 digits (no '+') as Meta expects.
   * Tunisian numbers are 8 digits — prepend the 216 country code so the
   * hashed value matches what Facebook stores (improves Event Match Quality).
   */
  _normalizePhone(value) {
    let digits = String(value).replace(/[^\d]/g, '');
    if (digits.startsWith('00')) digits = digits.slice(2);
    if (digits.length === 8) digits = '216' + digits; // local TN number
    return digits;
  }

  /**
   * Build a Facebook Advanced Matching object from persisted PII.
   * Values are sent RAW (normalized) — the pixel hashes them client-side.
   * Returns null until the customer is identified (email, phone or name):
   * fbevents only accepts user data on a second fbq('init') when the first one
   * had none, so a pixel must not be started with the country alone.
   */
  _getAdvancedMatching() {
    try {
      const pii = JSON.parse(localStorage.getItem('_fb_pii') || '{}');
      if (!pii.email && !pii.phone && !pii.firstName && !pii.lastName) return null;
      const norm = (v) => String(v).trim().toLowerCase();
      const am = {};
      if (pii.email)     am.em      = norm(pii.email);
      if (pii.phone)     am.ph      = this._normalizePhone(pii.phone);
      if (pii.firstName) am.fn      = norm(pii.firstName);
      if (pii.lastName)  am.ln      = norm(pii.lastName);
      if (pii.city)      am.ct      = norm(pii.city).replace(/\s+/g, '');
      if (pii.state)     am.st      = norm(pii.state).replace(/\s+/g, '');
      if (pii.zip)       am.zp      = norm(pii.zip).replace(/\s+/g, '');
      // Country is always known for this store (Tunisia-only). Sending it on
      // every event — even anonymous ones — lifts the customer-data match
      // coverage from ~35% to ~100% and is the main lever to reach EMQ >= 8.
      am.country = (pii.country ? norm(pii.country).slice(0, 2) : 'tn');
      try {
        const extId = localStorage.getItem('_fb_ext_id');
        if (extId) am.external_id = extId;
      } catch { /* ignore */ }
      return Object.keys(am).length ? am : null;
    } catch {
      return null;
    }
  }

  // ─── Helpers ────────────────────────────────────────────────────────

  /** SHA-256 hash (lowercase hex) — used to hash PII for CAPI user_data */
  async hashSHA256(text) {
    if (!text) return null;
    try {
      const normalized = String(text).toLowerCase().trim();
      const data = new TextEncoder().encode(normalized);
      const buf = await crypto.subtle.digest('SHA-256', data);
      return Array.from(new Uint8Array(buf)).map(b => b.toString(16).padStart(2, '0')).join('');
    } catch (e) {
      // crypto.subtle unavailable (Facebook in-app browser / non-HTTPS)
      return null;
    }
  }

  /** Read a cookie by name */
  _getCookie(name) {
    const m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
    return m ? decodeURIComponent(m[1]) : null;
  }

  /**
   * Get fbc value: prefer _fbc cookie, fallback to constructing from fbclid URL param.
   * Format: fb.1.{timestamp}.{fbclid}
   * When constructed from fbclid we persist it as a 90-day cookie so subsequent
   * events keep the click attribution (greatly improves Event Match Quality).
   */
  _getFbc() {
    const cookie = this._getCookie('_fbc');
    if (cookie) return cookie;
    try {
      const params = new URLSearchParams(window.location.search);
      const fbclid = params.get('fbclid');
      if (fbclid) {
        const val = `fb.1.${Date.now()}.${fbclid}`;
        try {
          document.cookie = `_fbc=${val}; path=/; max-age=${90 * 24 * 3600}; SameSite=Lax`;
        } catch { /* ignore */ }
        return val;
      }
    } catch { /* ignore */ }
    return null;
  }

  /**
   * Get fbp value: prefer the _fbp cookie set by the pixel. When the CAPI event
   * fires before the pixel has written the cookie (a common race that caps fbp
   * coverage at ~70%), generate a Meta-format browser ID ourselves and persist
   * it as a 90-day cookie. The pixel reuses an existing _fbp cookie, so this
   * keeps browser + server events consistent and pushes fbp coverage to ~100%.
   * Format: fb.1.{timestamp}.{random}
   */
  _getFbp() {
    const cookie = this._getCookie('_fbp');
    if (cookie) return cookie;
    try {
      const val = `fb.1.${Date.now()}.${Math.floor(Math.random() * 1e10)}`;
      try {
        document.cookie = `_fbp=${val}; path=/; max-age=${90 * 24 * 3600}; SameSite=Lax`;
      } catch { /* ignore */ }
      return val;
    } catch { /* ignore */ }
    return null;
  }

  /**
   * Get fb_login_id: the visitor's Facebook user ID.
   * This is ONLY available when the visitor authenticated through Facebook
   * Login (FB JS SDK). For a store that only loads the pixel (fbq), there is
   * no login session, so this returns null and the field is omitted.
   *
   * Resolution order:
   *  1. Live FB SDK auth response (window.FB.getAuthResponse().userID)
   *  2. Value persisted from a previous successful login (localStorage)
   * Sent UNHASHED as an integer, per Meta's spec for fb_login_id.
   */
  _getFbLoginId() {
    try {
      if (window.FB && typeof window.FB.getAuthResponse === 'function') {
        const auth = window.FB.getAuthResponse();
        if (auth && auth.userID) {
          try { localStorage.setItem('_fb_login_id', String(auth.userID)); } catch { /* ignore */ }
          return Number(auth.userID) || null;
        }
      }
    } catch { /* ignore */ }
    try {
      const stored = localStorage.getItem('_fb_login_id');
      if (stored) return Number(stored) || null;
    } catch { /* ignore */ }
    return null;
  }

  /** Stable anonymous visitor ID (external_id), created on first use. */
  _getExternalId() {
    try {
      let extId = localStorage.getItem('_fb_ext_id');
      if (!extId) {
        extId = `${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
        localStorage.setItem('_fb_ext_id', extId);
      }
      return extId;
    } catch {
      return null;
    }
  }

  /** Generate a unique event ID */
  _eventId(prefix) {
    return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  }

  /** True when the same event was already sent a moment ago. */
  _isRepeat(key) {
    const now = Date.now();
    const last = this._recent.get(key);
    this._recent.set(key, now);
    return last !== undefined && now - last < REPEAT_GUARD_MS;
  }

  // ─── Pixel Library ──────────────────────────────────────────────────

  /** Load the fbq library script. Safe to call multiple times. */
  _loadFbqLibrary() {
    if (this._fbqLoadPromise) return this._fbqLoadPromise;
    if (window.fbq) { this._fbqLoadPromise = Promise.resolve(true); return this._fbqLoadPromise; }

    this._fbqLoadPromise = new Promise((resolve) => {
      /* eslint-disable */
      !function(f,b,e,v,n,t,s){
        if(f.fbq) { resolve(true); return; }
        n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};
        if(!f._fbq)f._fbq=n;
        n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];
        t=b.createElement(e);t.async=!0;t.src=v;
        s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s);
        t.onload=()=>resolve(true);
        t.onerror=()=>resolve(false);
      }(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');
      /* eslint-enable */
      setTimeout(() => resolve(!!window.fbq), 5000);
    });
    return this._fbqLoadPromise;
  }

  /**
   * Ensure a specific pixel ID has been init-ed exactly once.
   * Must be called before any trackSingle call for that pixel.
   */
  async _ensurePixelInit(pixelId) {
    if (!pixelId) return false;
    await this._loadFbqLibrary();
    if (!window.fbq) return false;

    const am = this._getAdvancedMatching();

    if (!this._initedPixels.has(pixelId)) {
      // Attach Advanced Matching (hashed PII) at init when known — this raises
      // the browser-side Event Match Quality independently of the CAPI events.
      if (am) {
        window.fbq('init', pixelId, am);
        this._amAppliedPixels.add(pixelId);
      } else {
        window.fbq('init', pixelId);
      }
      this._initedPixels.add(pixelId);
    } else if (am && !this._amAppliedPixels.has(pixelId)) {
      // Re-init once the customer becomes identified to attach Advanced Matching
      // (accepted by fbevents because this pixel was started without user data).
      window.fbq('init', pixelId, am);
      this._amAppliedPixels.add(pixelId);
    }
    return true;
  }

  // ─── Initialization ─────────────────────────────────────────────────

  /**
   * Load the dollar rate of Paramétrage (event values are sent in USD).
   * No pixel here: pixels come from the product categories, and fbevents.js is
   * only loaded when one of them is used.
   */
  async initialize() {
    if (this.initializationPromise) return this.initializationPromise;

    this.initializationPromise = (async () => {
      try {
        if (!settingsLoader) {
          settingsLoader = import('../pages/admin/settings/controller/settings-controller')
            .then(m => m.fetchSettings())
            .finally(() => { settingsLoader = null; });
        }
        const settings = await settingsLoader;
        this.tauxDollar = toNumber(settings?.tauxDollar) || null;
        this.initialized = true;
        return true;
      } catch (err) {
        console.error('Failed to initialize Facebook Pixel:', err);
      }
      return false;
    })();

    return this.initializationPromise;
  }

  /** Wait for the initialization promise to resolve (if pending). */
  async _waitForInit() {
    if (this.initializationPromise) {
      try { await this.initializationPromise; } catch { /* already logged */ }
    }
  }

  /** The pixel of the product's category (none when the category has no pixel). */
  _targets(category) {
    const fromCategory = categoryPixel(category);
    return fromCategory ? [fromCategory] : [];
  }

  // ─── Backend CAPI Proxy ─────────────────────────────────────────────

  /**
   * Send an event to the Conversion API through the backend proxy.
   * The backend looks up the access_token from the DB by pixelId.
   * keepalive lets the request finish even if the page is left right after.
   */
  async _sendCAPI(pixelId, eventPayload) {
    if (!pixelId) return;
    try {
      const res = await fetch(`${API_URL}/FacebookConversion/event`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pixelId, data: [eventPayload] }),
        keepalive: true,
      });
      if (!res.ok) {
        const txt = await res.text();
        console.warn('CAPI proxy error:', txt);
      }
    } catch (e) {
      console.warn('CAPI proxy call failed:', e.message);
    }
  }

  /**
   * Build a CAPI user_data object with hashed PII + browser cookies.
   *
   * Customer PII is persisted to localStorage on first submission and replayed
   * on every subsequent event (PageView, ViewContent, AddToCart, ...). This is
   * the single biggest lever for Event Match Quality — without it, anonymous
   * top-of-funnel events score 5-7/10 instead of 8-10/10.
   */
  async _buildUserData() {
    const ud = { client_user_agent: navigator.userAgent };

    const fbp = this._getFbp();
    const fbc = this._getFbc();
    if (fbp) ud.fbp = fbp;
    if (fbc) ud.fbc = fbc;

    // fb_login_id: Facebook user ID (only present when the visitor used
    // Facebook Login). Sent UNHASHED. Improves Event Match Quality when set.
    const fbLoginId = this._getFbLoginId();
    if (fbLoginId) ud.fb_login_id = fbLoginId;

    // external_id: stable anonymous identifier (same value as the pixel's).
    const extId = this._getExternalId();
    const hashedExtId = extId ? await this.hashSHA256(extId) : null;
    if (hashedExtId) ud.external_id = [hashedExtId];

    let pii = {};
    try { pii = JSON.parse(localStorage.getItem('_fb_pii') || '{}'); } catch { /* ignore */ }

    // Meta requires strict normalization BEFORE hashing.
    const norm = (v) => String(v).trim().toLowerCase();
    const add = async (key, value) => {
      const hashed = value ? await this.hashSHA256(value) : null;
      if (hashed) ud[key] = [hashed];
    };

    if (pii.email)     await add('em', norm(pii.email));
    if (pii.phone)     await add('ph', this._normalizePhone(pii.phone));
    if (pii.firstName) await add('fn', norm(pii.firstName));
    if (pii.lastName)  await add('ln', norm(pii.lastName));
    if (pii.city)      await add('ct', norm(pii.city).replace(/\s+/g, ''));
    if (pii.state)     await add('st', norm(pii.state).replace(/\s+/g, ''));
    if (pii.zip)       await add('zp', norm(pii.zip).replace(/\s+/g, ''));
    // Country is always sent (default 'tn' for this Tunisia-only store) so every
    // event — including anonymous ViewContent — carries a hashed location match.
    await add('country', norm(pii.country || 'tn').slice(0, 2));

    return ud;
  }

  /**
   * Persist customer PII to localStorage so subsequent events automatically
   * include hashed em/ph/fn/ln/ct/st/zp/country in user_data.
   *
   * Call this as early as possible (e.g. on input blur in the checkout form).
   * It DOES NOT send any event — it only updates persisted state.
   *
   * Default country = 'TN' if not provided (most stores ship Tunisia only).
   */
  setCustomer(customer) {
    if (!customer || typeof customer !== 'object') return;
    try {
      const persisted = JSON.parse(localStorage.getItem('_fb_pii') || '{}');
      const next = { ...persisted };
      for (const k of Object.keys(customer)) {
        const v = customer[k];
        if (v != null && v !== '') next[k] = v;
      }
      if (!next.country) next.country = 'TN';
      localStorage.setItem('_fb_pii', JSON.stringify(next));
    } catch { /* ignore */ }
  }

  /**
   * Send one event to each target pixel: browser pixel + Conversions API
   * (when the server has a token for that pixel), with the same event_id.
   */
  /**
   * Meta does not accept TND ("Parameter 'currency' is invalid"): the value would be
   * dropped and the ads would show no purchase value. Amounts are converted to USD
   * with the dollar rate of Paramétrage (they stay in TND while no rate is set).
   */
  _inAcceptedCurrency(customData) {
    if (!customData || customData.currency !== 'TND' || !(this.tauxDollar > 0)) return customData;
    const usd = (v) => Math.round((toNumber(v) / this.tauxDollar) * 100) / 100;
    return {
      ...customData,
      value: usd(customData.value),
      currency: 'USD',
      ...(customData.contents
        ? { contents: customData.contents.map((c) => ({ ...c, item_price: usd(c.item_price) })) }
        : {}),
    };
  }

  async _send(targets, eventName, rawCustomData, { eventId, customer } = {}) {
    if (!targets.length) return false;
    // Known identity first, so both the pixel init and the CAPI user_data carry it.
    if (customer) this.setCustomer(customer);
    const customData = this._inAcceptedCurrency(rawCustomData);

    const id = eventId || this._eventId(eventName.toLowerCase());
    let userData = null;
    let sent = false;
    for (const target of targets) {
      const ok = await this._ensurePixelInit(target.pixelId);
      if (!ok) continue;
      window.fbq('trackSingle', target.pixelId, eventName, customData, { eventID: id });
      sent = true;

      if (target.hasToken) {
        userData = userData || await this._buildUserData();
        this._sendCAPI(target.pixelId, {
          event_name: eventName,
          event_time: Math.floor(Date.now() / 1000),
          event_id: id,
          action_source: 'website',
          event_source_url: window.location.href,
          user_data: userData,
          ...(customData && Object.keys(customData).length ? { custom_data: customData } : {}),
        });
      }
    }
    return sent;
  }

  /**
   * Cart events: each category pixel gets its own items (and its share of the
   * order value); items whose category has no pixel are not sent.
   * items: [{ productId, quantity, price, pixelId?, pixelHasToken? }]
   */
  async _sendCart(eventName, items, totalValue, { eventId, customer, orderId } = {}) {
    const subtotal = items.reduce((s, i) => s + toNumber(i.price) * (parseInt(i.quantity, 10) || 1), 0);
    const groups = new Map();
    for (const item of items) {
      const pixelId = String(item.pixelId || '').trim();
      if (!pixelId) continue;
      if (!groups.has(pixelId)) groups.set(pixelId, { target: { pixelId, hasToken: item.pixelHasToken !== false }, items: [] });
      groups.get(pixelId).items.push(item);
    }

    const sends = [...groups.values()].map(({ target, items: own }) => {
      const ownSubtotal = own.reduce((s, i) => s + toNumber(i.price) * (parseInt(i.quantity, 10) || 1), 0);
      const value = subtotal > 0 ? toNumber(totalValue) * ownSubtotal / subtotal : 0;
      const data = productData(toContents(own), value);
      if (orderId) data.order_id = String(orderId);
      return { target, data };
    });

    let sent = false;
    for (const { target, data } of sends) {
      sent = (await this._send([target], eventName, data, { eventId, customer })) || sent;
    }
    return sent;
  }

  // ─── Event: ViewContent ─────────────────────────────────────────────

  /**
   * Fire PageView + ViewContent on the category pixel when a user visits a product page.
   * @param {Object} data - { productId, productName, value, currency }
   * @param {Object|null} category - product.categorie
   */
  async trackViewContent(data, category = null) {
    try {
      await this._waitForInit();
      if (!data?.productId) return;
      if (this._isRepeat(`vc:${data.productId}`)) return;

      await this._send(this._targets(category), 'PageView', {});

      const customData = productData(
        toContents([{ productId: data.productId, quantity: 1, price: data.value }]),
        data.value,
        data.currency
      );
      if (data.productName) customData.content_name = data.productName;
      await this._send(this._targets(category), 'ViewContent', customData);
    } catch (e) {
      console.error('Error tracking ViewContent:', e);
    }
  }

  // ─── Event: AddToCart ───────────────────────────────────────────────

  /**
   * Fire AddToCart.
   * @param {Object} product - { id, price, name, quantity }
   * @param {Object|null} category - product.categorie
   */
  async trackAddToCart(product, category = null) {
    try {
      await this._waitForInit();
      if (!product?.id) return;
      const quantity = parseInt(product.quantity, 10) || 1;
      const customData = productData(
        toContents([{ productId: product.id, quantity, price: product.price }]),
        toNumber(product.price) * quantity
      );
      if (product.name) customData.content_name = product.name;
      await this._send(this._targets(category), 'AddToCart', customData);
    } catch (e) {
      console.error('Error tracking AddToCart:', e);
    }
  }

  // ─── Event: InitiateCheckout ────────────────────────────────────────

  /**
   * Fire InitiateCheckout for one product (product page order form).
   * @param {Object} data - { orderId, productId, quantity, price, value, currency, customer }
   * @param {Object|null} category - product.categorie
   */
  async trackInitiateCheckout(data, category = null) {
    try {
      await this._waitForInit();
      const customData = productData(
        toContents([{ productId: data?.productId, quantity: data?.quantity, price: data?.price ?? data?.value }]),
        data?.value,
        data?.currency
      );
      await this._send(this._targets(category), 'InitiateCheckout', customData, {
        eventId: data?.orderId ? `ic_${data.orderId}` : undefined,
        customer: data?.customer || (data?.phone ? { phone: data.phone } : null),
      });
    } catch (e) {
      console.error('Error tracking InitiateCheckout:', e);
    }
  }

  /**
   * Fire InitiateCheckout for the cart (checkout page).
   * @param {Object} data - { items, value }
   */
  async trackCartInitiateCheckout({ items = [], value } = {}) {
    try {
      await this._waitForInit();
      if (!items.length) return;
      if (this._isRepeat(`ic-cart:${items.map(i => `${i.productId}x${i.quantity}`).join(',')}`)) return;
      await this._sendCart('InitiateCheckout', items, value);
    } catch (e) {
      console.error('Error tracking InitiateCheckout:', e);
    }
  }

  // ─── Event: Lead ────────────────────────────────────────────────────

  /**
   * Track Lead: the customer left a phone number (abandoned order created).
   * @param {Object} data - { orderId, phone, value, currency, productId, customer }
   * @param {Object} category - product.categorie
   */
  async trackLead(data, category = null) {
    try {
      await this._waitForInit();
      const customData = {
        value: toNumber(data?.value),
        currency: data?.currency || 'TND',
        content_name: 'Abandoned Order',
        content_category: 'Ecommerce',
        content_ids: [String(data?.productId || '')].filter(Boolean),
        content_type: 'product',
      };
      await this._send(this._targets(category), 'Lead', customData, {
        eventId: data?.orderId ? `lead_${data.orderId}` : undefined,
        customer: data?.customer || (data?.phone ? { phone: data.phone } : null),
      });
    } catch (e) {
      console.error('Error tracking Lead:', e);
    }
  }

  // ─── Event: Purchase ────────────────────────────────────────────────

  /**
   * Track Purchase of one product order (product page).
   * The event_id is derived from the order: sent twice, it is counted once.
   * @param {Object} orderData - { orderId, value, currency, items[], customer{} }
   * @param {Object|null} category - product.categorie
   */
  async trackPurchase(orderData, category = null) {
    try {
      await this._waitForInit();
      if (!orderData?.value) return { success: false, reason: 'invalid_data' };
      const targets = this._targets(category);
      if (!targets.length) return { success: false, reason: 'no_pixel' };

      const items = Array.isArray(orderData.items) ? orderData.items : [];
      const customData = productData(toContents(items), orderData.value, orderData.currency);
      if (orderData.orderId) customData.order_id = String(orderData.orderId);

      const sent = await this._send(targets, 'Purchase', customData, {
        eventId: orderData.orderId ? `purchase_${orderData.orderId}` : undefined,
        customer: orderData.customer,
      });
      return sent ? { success: true } : { success: false, reason: 'pixel_not_loaded' };
    } catch (e) {
      console.error('Error tracking Purchase:', e);
      return { success: false, error: e.message };
    }
  }

  /**
   * Track Purchase of a cart order (checkout page): split per category pixel.
   * @param {Object} orderData - { orderId, value, items[], customer{} }
   */
  async trackCartPurchase({ orderId, value, items = [], customer } = {}) {
    try {
      await this._waitForInit();
      if (!value || !items.length) return { success: false, reason: 'invalid_data' };
      const sent = await this._sendCart('Purchase', items, value, {
        eventId: orderId ? `purchase_${orderId}` : undefined,
        customer,
        orderId,
      });
      return sent ? { success: true } : { success: false, reason: 'no_pixel' };
    } catch (e) {
      console.error('Error tracking Purchase:', e);
      return { success: false, error: e.message };
    }
  }

  // ─── Debug ──────────────────────────────────────────────────────────

  debugPixelStatus() {
    console.log('=== Facebook Pixel Debug ===');
    console.log('fbq loaded:', !!window.fbq);
    console.log('Inited pixels (categories):', [...this._initedPixels]);
    console.log('============================');
  }
}

const facebookConversionService = new FacebookConversionService();

if (typeof window !== 'undefined') {
  window.debugFacebookPixel = () => facebookConversionService.debugPixelStatus();
}

export default facebookConversionService;
